import { z } from 'zod';

import type { NumberSettingKey } from '@/features/config/model/app-setting';

export const SUPPORT_TICKET_STATUSES = [
  'open',
  'in_progress',
  'waiting_on_user',
  'resolved',
  'closed',
] as const;
export type SupportTicketStatus = (typeof SUPPORT_TICKET_STATUSES)[number];

export const SUPPORT_TICKET_PRIORITIES = ['low', 'normal', 'high', 'urgent'] as const;
export type SupportTicketPriority = (typeof SUPPORT_TICKET_PRIORITIES)[number];

export const SUPPORT_TICKET_CATEGORIES = ['user', 'system_flag', 'other'] as const;
export type SupportTicketCategory = (typeof SUPPORT_TICKET_CATEGORIES)[number];

export const SUPPORT_TICKET_ROLES = [
  'requester',
  'manager',
  'freelancer',
  'appointed',
  'certifier',
  'admin',
  'system',
] as const;
export type SupportTicketRole = (typeof SUPPORT_TICKET_ROLES)[number];

export function isTerminal(status: SupportTicketStatus): boolean {
  return status === 'resolved' || status === 'closed';
}

export interface SupportTicket {
  readonly id: string;
  readonly createdBy: string | null;
  readonly createdByName: string | null;
  readonly roleAtCreation: SupportTicketRole;
  readonly subject: string;
  readonly category: SupportTicketCategory;
  readonly status: SupportTicketStatus;
  readonly priority: SupportTicketPriority;
  readonly assignedAdminId: string | null;
  readonly assignedAdminName: string | null;
  readonly relatedApplicationId: string | null;
  readonly relatedMasjidId: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly firstResponseAt: string | null;
  readonly resolvedAt: string | null;
}

const rowSchema = z.object({
  id: z.string(),
  created_by: z.string().nullable(),
  role_at_creation: z.enum(SUPPORT_TICKET_ROLES),
  subject: z.string(),
  category: z.enum(SUPPORT_TICKET_CATEGORIES),
  status: z.enum(SUPPORT_TICKET_STATUSES),
  priority: z.enum(SUPPORT_TICKET_PRIORITIES),
  assigned_admin_id: z.string().nullable(),
  related_application_id: z.string().nullable(),
  related_masjid_id: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  first_response_at: z.string().nullable(),
  resolved_at: z.string().nullable(),
  creator: z.object({ full_name: z.string() }).nullable().optional(),
  assignee: z.object({ full_name: z.string() }).nullable().optional(),
});

export function mapSupportTicket(row: unknown): SupportTicket {
  const r = rowSchema.parse(row);
  return {
    id: r.id,
    createdBy: r.created_by,
    createdByName: r.creator?.full_name ?? null,
    roleAtCreation: r.role_at_creation,
    subject: r.subject,
    category: r.category,
    status: r.status,
    priority: r.priority,
    assignedAdminId: r.assigned_admin_id,
    assignedAdminName: r.assignee?.full_name ?? null,
    relatedApplicationId: r.related_application_id,
    relatedMasjidId: r.related_masjid_id,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
    firstResponseAt: r.first_response_at,
    resolvedAt: r.resolved_at,
  };
}

/**
 * D4: a ticket is overdue when it has never received a first response and the
 * SLA target (hours, keyed by priority via `app_settings.sla.response_hours_*`)
 * has elapsed since creation. Terminal tickets are never overdue.
 */
export function isOverdue(
  ticket: Pick<SupportTicket, 'status' | 'firstResponseAt' | 'createdAt'>,
  slaHours: number,
): boolean {
  if (isTerminal(ticket.status)) return false;
  if (ticket.firstResponseAt !== null) return false;
  const deadline = new Date(ticket.createdAt).getTime() + slaHours * 3_600_000;
  return Date.now() > deadline;
}

/** Whole hours elapsed since ticket creation — used for the SLA-age display. */
export function hoursSinceCreated(createdAt: string): number {
  return Math.max(0, Math.floor((Date.now() - new Date(createdAt).getTime()) / 3_600_000));
}

/** Maps a ticket priority to its `app_settings` SLA response-hours key. */
export function slaSettingKeyForPriority(priority: SupportTicketPriority): NumberSettingKey {
  return `sla.response_hours_${priority}` as NumberSettingKey;
}
